import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../../partials/Header';
import Footer from '../../partials/Footer';
import PageIllustration from '../../partials/PageIllustration';
import { IPFS_GATEWAYS, parseIpfsInput, gatewayUrl, inspectCid, SAMPLE_CIDS } from '../../utils/ipfs';

const TIMEOUT_MS = 10000;

// HEAD first (headers only, nothing to abort); a few gateways reject it
// with 405 — fall back to GET and abort the body once headers arrive.
async function probeGateway(host, cid, path) {
  const url = gatewayUrl(host, cid, path);
  const start = performance.now();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    let res = await fetch(url, {
      method: 'HEAD',
      signal: controller.signal,
      cache: 'no-store',
      credentials: 'omit',
    });
    if (res.status === 405 || res.status === 501) {
      res = await fetch(url, {
        method: 'GET',
        signal: controller.signal,
        cache: 'no-store',
        credentials: 'omit',
      });
      // Headers received — abort before the body downloads
      controller.abort();
    }
    const latency = Math.round(performance.now() - start);
    return { status: res.status, ok: res.ok, latency, url };
  } catch (e) {
    const latency = Math.round(performance.now() - start);
    const aborted = e.name === 'AbortError';
    return { status: null, ok: false, latency, url, error: aborted ? 'timeout' : 'cors/network' };
  } finally {
    clearTimeout(timer);
  }
}

function statusLabel(result) {
  if (!result) return { text: 'pending', cls: 'text-gray-500' };
  if (result.ok) return { text: `${result.status} OK`, cls: 'text-green-400' };
  if (result.status === 404) return { text: '404 — not found here', cls: 'text-yellow-400' };
  if (result.status === 410 || result.status === 451) return { text: `${result.status} — blocked by gateway`, cls: 'text-orange-400' };
  if (result.status === 429) return { text: '429 — rate limited', cls: 'text-yellow-400' };
  if (result.status) return { text: `${result.status}`, cls: 'text-yellow-400' };
  if (result.error === 'timeout') return { text: 'timeout', cls: 'text-red-400' };
  return { text: 'unreachable / CORS blocked', cls: 'text-red-400' };
}

function IpfsGatewayChecker() {
  const [input, setInput] = useState('');
  const [checking, setChecking] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [target, setTarget] = useState(null);

  const check = async () => {
    setError('');
    setResults(null);
    let parsed;
    try {
      parsed = parseIpfsInput(input);
      if (parsed.kind !== 'ipfs') throw new Error('That looks like an IPNS name — the checker tests IPFS CIDs (the name must resolve to one first)');
      // Validate the CID up front
      inspectCid(parsed.ref);
    } catch (e) {
      setError(e.message);
      return;
    }
    setTarget(parsed);
    setChecking(true);
    const initial = IPFS_GATEWAYS.map((g) => ({ ...g, result: null }));
    setResults([...initial]);
    await Promise.all(
      IPFS_GATEWAYS.map(async (g, i) => {
        const result = await probeGateway(g.host, parsed.ref, parsed.path);
        setResults((prev) => {
          const next = [...prev];
          next[i] = { ...next[i], result };
          return next;
        });
      })
    );
    setChecking(false);
  };

  const reachable = results ? results.filter((r) => r.result?.ok).length : 0;
  const best = results?.filter((r) => r.result?.ok).sort((a, b) => a.result.latency - b.result.latency)[0];

  return (
    <div className="flex flex-col min-h-screen overflow-hidden">
      <Helmet>
        <title>IPFS Gateway Checker — Test CID Availability</title>
        <meta name="description" content="Check whether your IPFS CID is reachable across public gateways — availability and latency test with working gateway URLs. Free, no signup, in-browser." />
        <meta name="keywords" content="ipfs gateway checker, ipfs gateway test, ipfs cid availability, ipfs gateway status, public ipfs gateway, ipfs online checker" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.ogtechnologies.co/tools/ipfs-gateway-checker/" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.ogtechnologies.co/tools/ipfs-gateway-checker/" />
        <meta property="og:title" content="IPFS Gateway Checker — Test CID Availability" />
        <meta property="og:description" content="Check whether your IPFS CID is reachable across public gateways — availability and latency with working URLs." />
        <meta property="og:image" content="https://www.ogtechnologies.co/og-og-image.png" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.ogtechnologies.co/tools/ipfs-gateway-checker/" />
        <meta name="twitter:title" content="IPFS Gateway Checker — Test CID Availability" />
        <meta name="twitter:description" content="Check whether your IPFS CID is reachable across public gateways — availability and latency with working URLs." />
        <meta name="twitter:image" content="https://www.ogtechnologies.co/og-og-image.png" />
      </Helmet>

      <Header />

      <main className="grow">
        <div className="relative max-w-6xl mx-auto h-0 pointer-events-none" aria-hidden="true">
          <PageIllustration />
        </div>

        <section className="relative">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="pt-32 pb-12 md:pt-40 md:pb-20">
              <div className="max-w-3xl mx-auto text-center pb-12 md:pb-16">
                <h1 className="h1">IPFS Gateway Checker</h1>
                <p className="text-xl text-gray-400 mt-4">
                  Test whether a CID is retrievable through the major public HTTP gateways — with per-gateway status,
                  latency, and ready-to-use links.
                </p>
              </div>

              <div className="max-w-4xl mx-auto">
                <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
                  <div className="mb-4">
                    <label className="block text-gray-300 text-sm font-medium mb-1">CID, ipfs:// URI, or gateway URL</label>
                    <input
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') check(); }}
                      className="w-full bg-gray-700 text-white px-4 py-2 rounded-md border border-gray-600 focus:border-purple-500 focus:outline-none font-mono text-sm"
                      placeholder="bafybei… or ipfs://bafybei…/file.png or https://ipfs.io/ipfs/…"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      A path after the CID (e.g. /metadata.json) is preserved. Try:{' '}
                      <button type="button" className="text-purple-400 hover:text-purple-300" onClick={() => setInput(SAMPLE_CIDS.v1)}>sample CID</button>
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={check}
                    disabled={checking || !input.trim()}
                    className="w-full px-4 py-3 rounded-md bg-purple-600 text-white font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mb-4"
                  >
                    {checking ? 'Checking gateways…' : 'Check Availability'}
                  </button>

                  {error && <p className="text-sm text-red-400 mb-4">{error}</p>}

                  {results && target && (
                    <div>
                      <p className="text-sm text-gray-400 mb-3">
                        {checking
                          ? `Probing ${IPFS_GATEWAYS.length} gateways for ${target.ref.slice(0, 16)}…`
                          : `${reachable} of ${IPFS_GATEWAYS.length} gateways can serve this CID.`}
                        {best && !checking && (
                          <>
                            {' '}Fastest: <span className="text-gray-200">{best.host}</span> ({best.result.latency} ms)
                          </>
                        )}
                      </p>
                      <div className="divide-y divide-gray-700/60 border border-gray-700 rounded-md overflow-hidden">
                        {results.map((g) => {
                          const s = statusLabel(g.result);
                          return (
                            <div key={g.host} className="flex items-center justify-between gap-4 px-4 py-3 bg-gray-900/40">
                              <div className="min-w-0">
                                <p className="text-sm text-gray-200 font-medium truncate">{g.host}</p>
                                <p className="text-xs text-gray-500">{g.operator}</p>
                              </div>
                              <div className="flex items-center gap-4 shrink-0">
                                {g.result?.latency != null && (
                                  <span className="text-xs text-gray-500 w-16 text-right">{g.result.latency} ms</span>
                                )}
                                <span className={`text-xs font-medium w-44 text-right ${s.cls}`}>
                                  {!g.result ? 'checking…' : s.text}
                                </span>
                                {g.result?.ok ? (
                                  <a
                                    href={g.result.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-purple-400 hover:text-purple-300 w-10"
                                  >
                                    Open
                                  </a>
                                ) : (
                                  <span className="w-10" />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="mt-6 text-xs text-gray-500 space-y-1">
                    <p><strong>404</strong> means the gateway is up but cannot find providers for this CID — pin it somewhere (Pinata, web3.storage, your own node) before sharing links.</p>
                    <p><strong>CORS blocked</strong> means the gateway refused a browser request — it may still respond to curl or server-side calls.</p>
                    <p className="text-yellow-500/80">Public gateways are best-effort and sometimes flagged for phishing. For production, pin content and prefer a dedicated or self-hosted gateway.</p>
                  </div>
                </div>

                <div className="mt-6 text-center text-sm text-gray-500">
                  More IPFS tools:{' '}
                  <Link to="/tools/ipfs-cid-inspector/" className="text-purple-400 hover:text-purple-300">CID Inspector</Link>
                  {' · '}
                  <Link to="/tools/ipfs-cid-generator/" className="text-purple-400 hover:text-purple-300">File → CID</Link>
                  {' · '}
                  <Link to="/tools/ipfs-url-converter/" className="text-purple-400 hover:text-purple-300">URL Converter</Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default IpfsGatewayChecker;
